import { Dispatch, useEffect } from 'react';

import { v4 as UUIDv4 } from 'uuid';

import {
	LDContext,
	useFlags,
	useLDClient,
} from 'launchdarkly-react-client-sdk';
import { AnyAction } from 'redux';

import { FeatureFlagsKeysEnum } from 'common/constants/FeatureFlags';
import { setIsLDResponseAvailable } from 'common/store/reducer/commonReducer';

import { getPredefinedFeatures } from '../common/utils/feature';

export const useOrganizationFeatureFlags = (
	userData: any,
	isLoggedIn: boolean,
	dispatch: Dispatch<AnyAction>,
) => {
	const ldClient = useLDClient();
	const setLDContext = async () => {
		const newContext: LDContext = {
			kind: 'multi',
			user: {
				key: userData.user.userId, // Optional: Include a unique user key
				// kind: 'user',
				email: userData.user.email,
				name: userData.user.name,
				jobFunction: userData.user.role,
				group: userData.user.group,
				tenant: userData.user.tenantId,
			},
			organization: {
				key: UUIDv4(), // Optional: Include a unique organization key
				name: userData.organization.name,
			},
			application: {
				key: process.env.NODE_ENV || 'development', // Include the environment key or fallback to a default value
				version: '0.1.5',
			},
		};
		const response = await ldClient?.identify(newContext);
		if (response) {
			dispatch(setIsLDResponseAvailable(true));
		}
	};

	useEffect(() => {
		if (isLoggedIn) {
			setLDContext(); // after login
		}
	}, [ldClient, userData.user.email]);

	return ldClient;
};

export const useFeatureFlag = (
	key: FeatureFlagsKeysEnum,
	isLDResponseAvailable = true,
): boolean => {
	const { [key]: featureFlag } = useFlags();
	const fallbackValue = false;
	const value = getPredefinedFeatures()[key];
	const defaultValue = prepareBooleanStringFeatureFlag(value, fallbackValue);
	if (!isLDResponseAvailable) {
		return false;
	}
	return featureFlag ?? defaultValue;
};

export const prepareBooleanStringFeatureFlag = (
	value: string | undefined,
	defaultValue: boolean,
) => {
	if (!value) {
		return defaultValue;
	}
	return value === 'true';
};

export const useStringFeatureFlag = (key: FeatureFlagsKeysEnum): string => {
	const { [key]: featureFlag } = useFlags();
	const value = getPredefinedFeatures()[key];
	return featureFlag ?? value;
};
